import type { Card, CreateCardBody } from '@klopotun/contracts';
import { useCallback, useState } from 'react';
import {
  useCardArchiveMutation,
  useCardsQuery,
  useDeleteCardMutation,
  useSaveCardMutation,
  type SaveCardInput,
} from '../../../entities/card/index.js';
import { useMeQuery } from '../../../entities/me/index.js';
import {
  CardForm,
  emptyCardFormValues,
  toCardFormValues,
} from '../../../features/card-form/index.js';
import { ApiError } from '../../../shared/api/index.js';
import { BackButton } from '../../../shared/telegram/index.js';
import {
  EmptyState,
  ErrorState,
  Fab,
  List,
  ListItem,
  SegmentedControl,
  Sheet,
  Spinner,
  type SegmentedOption,
  type SheetAction,
} from '../../../shared/ui/index.js';
import styles from './cards-section.module.css';

type CardScope = 'live' | 'archive';

type CardEditor = { mode: 'create' } | { mode: 'edit'; card: Card };

const SCOPE_OPTIONS: SegmentedOption[] = [
  { value: 'live', label: 'Активні' },
  { value: 'archive', label: 'Архів' },
];

const scopeOf = (value: string): CardScope => (value === 'archive' ? 'archive' : 'live');

const isConflict = (error: Error | null): boolean =>
  error instanceof ApiError && error.status === 409;

const subtitleOf = (card: Card): string | undefined => {
  const parts = [card.brand, card.last4 === null ? null : `•••• ${card.last4}`].filter(
    (part): part is string => part !== null,
  );

  return parts.length === 0 ? undefined : parts.join(' · ');
};

type CardsListProps = {
  cards: Card[] | undefined;
  scope: CardScope;
  isPending: boolean;
  errorMessage: string | null;
  onRetry: () => void;
  onSelect: (card: Card) => void;
};

const CardsList = ({
  cards,
  scope,
  isPending,
  errorMessage,
  onRetry,
  onSelect,
}: CardsListProps) => {
  if (errorMessage !== null) {
    return (
      <ErrorState title="Не вдалося завантажити картки" message={errorMessage} onRetry={onRetry} />
    );
  }

  if (isPending || cards === undefined) {
    return (
      <div className={styles.centered}>
        <Spinner label="Завантаження" />
      </div>
    );
  }

  const shown = cards.filter((card) => card.isArchived === (scope === 'archive'));

  if (shown.length === 0) {
    return (
      <EmptyState
        title={scope === 'archive' ? 'В архіві порожньо' : 'Карток ще немає'}
        description={
          scope === 'archive'
            ? 'Сюди потрапляють картки, сховані з переліку вибору.'
            : 'Додайте картку, щоб бачити, звідки списуються платежі.'
        }
      />
    );
  }

  return (
    <List>
      {shown.map((card) => (
        <ListItem
          key={card.id}
          title={card.label}
          subtitle={subtitleOf(card)}
          value={card.currency ?? undefined}
          chevron
          onClick={() => {
            onSelect(card);
          }}
        />
      ))}
    </List>
  );
};

export const CardsSection = () => {
  const [scope, setScope] = useState<CardScope>('live');
  const [editor, setEditor] = useState<CardEditor | null>(null);
  const [selectedCard, setSelectedCard] = useState<Card | null>(null);
  const [cardToDelete, setCardToDelete] = useState<Card | null>(null);
  const [cardInUse, setCardInUse] = useState<Card | null>(null);
  const me = useMeQuery();
  const cards = useCardsQuery({ includeArchived: true });
  const saveCard = useSaveCardMutation();
  const archiveCard = useCardArchiveMutation();
  const deleteCard = useDeleteCardMutation();
  const closeEditor = useCallback(() => {
    setEditor(null);
  }, []);

  const save = (body: CreateCardBody): void => {
    const input: SaveCardInput =
      editor?.mode === 'edit'
        ? { mode: 'update', id: editor.card.id, body }
        : { mode: 'create', body };

    saveCard.mutate(input, { onSuccess: closeEditor });
  };

  const toggleArchive = (card: Card): void => {
    archiveCard.mutate(
      { action: card.isArchived ? 'restore' : 'archive', id: card.id },
      {
        onSuccess: () => {
          setSelectedCard(null);
          setCardInUse(null);
        },
      },
    );
  };

  const remove = (card: Card): void => {
    deleteCard.mutate(card.id, {
      onSuccess: () => {
        setCardToDelete(null);
      },
      onError: (failure) => {
        setCardToDelete(null);

        if (isConflict(failure)) {
          setCardInUse(card);
        }
      },
    });
  };

  if (editor !== null) {
    if (me.isPending) {
      return (
        <div className={styles.centered}>
          <Spinner label="Завантаження" />
        </div>
      );
    }

    if (me.isError) {
      return (
        <ErrorState
          title="Не вдалося відкрити форму"
          message={me.error.message}
          onRetry={() => {
            void me.refetch();
          }}
        />
      );
    }

    return (
      <div className={styles.editor}>
        <BackButton onBack={closeEditor} />
        <h2 className={styles.subtitle}>
          {editor.mode === 'create' ? 'Нова картка' : 'Редагування картки'}
        </h2>

        {saveCard.error !== null && (
          <p className={styles.actionError} role="alert">
            {saveCard.error.message}
          </p>
        )}

        <CardForm
          initialValues={
            editor.mode === 'create'
              ? emptyCardFormValues(me.data.settings.baseCurrency)
              : toCardFormValues(editor.card)
          }
          busy={saveCard.isPending}
          onSubmit={save}
          onCancel={closeEditor}
        />
      </div>
    );
  }

  const cardActions: SheetAction[] =
    selectedCard === null
      ? []
      : [
          {
            id: 'edit',
            label: 'Змінити',
            onSelect: () => {
              setEditor({ mode: 'edit', card: selectedCard });
              setSelectedCard(null);
            },
          },
          {
            id: 'archive',
            label: selectedCard.isArchived ? 'Повернути з архіву' : 'Сховати з переліку',
            disabled: archiveCard.isPending,
            onSelect: () => {
              toggleArchive(selectedCard);
            },
          },
          {
            id: 'delete',
            label: 'Видалити назавжди',
            danger: true,
            onSelect: () => {
              setCardToDelete(selectedCard);
              setSelectedCard(null);
            },
          },
        ];

  const deleteConfirmation: SheetAction[] =
    cardToDelete === null
      ? []
      : [
          {
            id: 'confirm-delete',
            label: 'Так, видалити',
            danger: true,
            disabled: deleteCard.isPending,
            onSelect: () => {
              remove(cardToDelete);
            },
          },
        ];

  const conflictActions: SheetAction[] =
    cardInUse === null
      ? []
      : [
          {
            id: 'archive-instead',
            label: 'Сховати з переліку',
            disabled: archiveCard.isPending,
            onSelect: () => {
              toggleArchive(cardInUse);
            },
          },
        ];

  const deleteErrorText =
    deleteCard.error === null || isConflict(deleteCard.error) ? null : deleteCard.error.message;

  return (
    <>
      <SegmentedControl
        label="Що показувати"
        name="card-scope"
        options={SCOPE_OPTIONS}
        value={scope}
        hideLabel
        onChange={(value) => {
          setScope(scopeOf(value));
        }}
      />

      {deleteErrorText !== null && (
        <p className={styles.actionError} role="alert">
          {deleteErrorText}
        </p>
      )}

      <CardsList
        cards={cards.data}
        scope={scope}
        isPending={cards.isPending}
        errorMessage={cards.error === null ? null : cards.error.message}
        onRetry={() => {
          void cards.refetch();
        }}
        onSelect={setSelectedCard}
      />

      <Fab
        label="Додати картку"
        onClick={() => {
          setEditor({ mode: 'create' });
        }}
      />

      <Sheet
        open={selectedCard !== null}
        actions={cardActions}
        title={selectedCard?.label}
        onClose={() => {
          setSelectedCard(null);
        }}
      />

      <Sheet
        open={cardToDelete !== null}
        actions={deleteConfirmation}
        title="Видалити картку? Це не відкотиться."
        closeLabel="Ні, лишити"
        onClose={() => {
          setCardToDelete(null);
        }}
      />

      <Sheet
        open={cardInUse !== null}
        actions={conflictActions}
        title="Цю картку використовує підписка або рахунок банку, тому видалити її не можна. Її можна сховати з переліку — підписки й рахунки лишаться цілими."
        closeLabel="Нічого не робити"
        onClose={() => {
          setCardInUse(null);
        }}
      />
    </>
  );
};
